(function () {
  if (customElements.get('product-text-field')) return;

  // Campo testo (es. segno zodiacale). Il valore viene copiato in un input hidden
  // dentro il product-form come properties[...], così finisce in carrello/ordine
  // senza fee product né logica add-on.
  //
  // form_required (data-required): se false il campo è facoltativo.
  // Shop Pay / PayPal non passano dal submit: con campo obbligatorio li
  // blocchiamo con un gate finché non è valido.
  //
  // _melide_snapshot: copia tecnica IT per Order Printer (stesso formato del ricamo).
  // Sui bundle nativi le properties finiscono sul lineItemGroup: Order Printer
  // NON le legge. Copia anche in attributes[_melide_c_{variantId}] (OP le legge).
  const MELIDE_SNAPSHOT_PROPERTY = '_melide_snapshot';
  const MELIDE_CART_ATTR_PREFIX = '_melide_c_';

  function serializeMelideSnapshot(entries, { productTitle, variantId, variantTitle } = {}) {
    const parts = [];
    if (productTitle) parts.push(`_bundle::${productTitle}`);
    if (variantId) parts.push(`_variant_id::${variantId}`);
    if (variantTitle) parts.push(`_variant::${variantTitle}`);
    for (const [key, value] of entries) {
      if (key && value) parts.push(`${key}::${value}`);
    }
    return parts.join('|');
  }

  class ProductTextField extends HTMLElement {
    connectedCallback() {
      this.productForm =
        this.closest('product-form') || document.querySelector('product-form');
      this.form =
        this.productForm?.querySelector('form') ||
        document.querySelector('form[data-type="add-to-cart-form"]');
      this.input = this.querySelector('[data-text-field-input]');
      this.errorEl = this.querySelector('[data-text-field-error]');
      this.hintEl = this.querySelector('[data-text-field-hint]');
      this.propertyName = this.dataset.propertyName || 'Informazione richiesta';
      this.productTitle = this.dataset.productTitle || '';
      this.minChars = parseInt(this.dataset.minChars || '1', 10);
      this.maxChars = parseInt(this.dataset.maxChars || '50', 10);
      this.onlyNumbers = this.dataset.onlyNumbers === 'true';
      this.required = this.dataset.required !== 'false';

      this.ensureHiddenInput();
      this.ensureSnapshotInput();
      this.ensureAttributeInput();

      if (typeof subscribe === 'function') {
        this.variantChangeUnsubscriber = subscribe(
          typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.variantChange : 'variant-change',
          this.onVariantChange.bind(this)
        );
      }

      if (this.required && this.productForm) {
        this.productForm.setAttribute('data-require-text-field', 'true');
      }

      this.ensureCheckoutGate();
      this.syncCheckoutGate();
      this.updateMaxHint();

      if (this.input) {
        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('blur', this.onInput.bind(this));
      }

      if (this.form) {
        this.form.addEventListener('submit', this.onFormSubmit.bind(this), true);
      }

      this.onLockedSubmitClick = this.onLockedSubmitClick.bind(this);
      if (this.required) {
        document.addEventListener('click', this.onLockedSubmitClick, true);
        this.syncSubmitButtons(false);
      }

      this.paymentObserver = new MutationObserver(() => {
        this.ensureCheckoutGate();
        this.syncCheckoutGate();
        this.syncSubmitButtons(this.validate().ok);
      });
      if (this.form) {
        this.paymentObserver.observe(this.form, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      this.paymentObserver?.disconnect();
      this.variantChangeUnsubscriber?.();
      if (this.onLockedSubmitClick) {
        document.removeEventListener('click', this.onLockedSubmitClick, true);
      }
    }

    getLinkedSubmitButtons() {
      const buttons = [];
      const seen = new Set();
      const push = (btn) => {
        if (!btn || seen.has(btn)) return;
        seen.add(btn);
        buttons.push(btn);
      };

      push(this.productForm?.querySelector('.product-form__submit'));
      const formId = this.form?.id;
      if (formId) {
        document
          .querySelectorAll(
            `button[type="submit"][form="${CSS.escape(formId)}"], .product-bar__submit[form="${CSS.escape(formId)}"]`
          )
          .forEach(push);
      }
      document.querySelectorAll('.product-bar__submit').forEach(push);
      return buttons;
    }

    syncSubmitButtons(enabled) {
      if (!this.required) return;
      this.getLinkedSubmitButtons().forEach((btn) => {
        if (btn.disabled) return;
        if (enabled) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-text-field-locked');
        } else {
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('is-text-field-locked');
        }
      });
    }

    onLockedSubmitClick(event) {
      if (!this.required || this.validate().ok) return;

      const btn = event.target.closest(
        '.product-bar__submit, .product-form__submit, button[name="add"][type="submit"]'
      );
      if (!btn) return;

      const formId = this.form?.id;
      const linked =
        this.productForm?.contains(btn) ||
        btn.classList.contains('product-bar__submit') ||
        (formId && btn.getAttribute('form') === formId);
      if (!linked) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.blockIncompleteCheckout();
    }

    ensureHiddenInput() {
      if (!this.form) return;

      const selector = `[data-text-field-hidden="${CSS.escape(this.propertyName)}"]`;
      this.hiddenInput = this.form.querySelector(selector);

      if (!this.hiddenInput) {
        this.hiddenInput = document.createElement('input');
        this.hiddenInput.type = 'hidden';
        this.hiddenInput.name = `properties[${this.propertyName}]`;
        this.hiddenInput.setAttribute('data-text-field-hidden', this.propertyName);
        this.form.appendChild(this.hiddenInput);
      }
    }

    ensureSnapshotInput() {
      if (!this.form) return;

      this.snapshotInput = this.form.querySelector('[data-text-field-snapshot]');
      if (!this.snapshotInput) {
        this.snapshotInput = document.createElement('input');
        this.snapshotInput.type = 'hidden';
        this.snapshotInput.name = `properties[${MELIDE_SNAPSHOT_PROPERTY}]`;
        this.snapshotInput.setAttribute('data-text-field-snapshot', '');
        this.form.appendChild(this.snapshotInput);
      }
    }

    ensureAttributeInput() {
      if (!this.form) return;

      this.attributeInput = this.form.querySelector('[data-text-field-attribute]');
      if (!this.attributeInput) {
        this.attributeInput = document.createElement('input');
        this.attributeInput.type = 'hidden';
        this.attributeInput.setAttribute('data-text-field-attribute', '');
        this.form.appendChild(this.attributeInput);
      }
    }

    onVariantChange(event) {
      const variant = event?.data?.variant;
      if (variant?.id) {
        this.currentVariantId = String(variant.id);
        this.currentVariantTitle = variant.title || '';
      }
      this.syncHiddenInput();
    }

    getVariantId() {
      return (
        this.currentVariantId ||
        this.form?.querySelector('input[name="id"]')?.value ||
        ''
      );
    }

    getVariantTitle() {
      return this.currentVariantTitle || '';
    }

    getPaymentButtonRoot() {
      if (!this.form) return null;
      return (
        this.form.querySelector('.shopify-payment-button') ||
        this.form.querySelector('[data-shopify="payment-button"]')
      );
    }

    ensureCheckoutGate() {
      if (!this.required) {
        if (this.checkoutGate) this.checkoutGate.hidden = true;
        return;
      }

      const paymentRoot = this.getPaymentButtonRoot();
      if (!paymentRoot) return;

      if (getComputedStyle(paymentRoot).position === 'static') {
        paymentRoot.style.position = 'relative';
      }

      this.checkoutGate = paymentRoot.querySelector('[data-text-field-checkout-gate]');
      if (!this.checkoutGate) {
        this.checkoutGate = document.createElement('button');
        this.checkoutGate.type = 'button';
        this.checkoutGate.setAttribute('data-text-field-checkout-gate', '');
        this.checkoutGate.className = 'product-text-field__checkout-gate';
        this.checkoutGate.setAttribute(
          'aria-label',
          'Compila il campo obbligatorio prima di procedere'
        );
        this.checkoutGate.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.blockIncompleteCheckout();
        });
        paymentRoot.appendChild(this.checkoutGate);
      }
    }

    syncCheckoutGate() {
      const valid = this.validate().ok;
      // Deve essere letteralmente "true": ProductForm confronta getAttribute(...) === "true".
      // toggleAttribute lascia value="" e blocca l'add anche a campo compilato.
      if (valid) {
        this.productForm?.setAttribute('data-text-field-ready', 'true');
      } else {
        this.productForm?.removeAttribute('data-text-field-ready');
      }
      this.syncSubmitButtons(valid);

      if (!this.required) {
        this.form?.classList.remove('product-form--text-field-incomplete');
        this.productForm?.classList.remove('product-form--text-field-incomplete');
        if (this.checkoutGate) this.checkoutGate.hidden = true;
        return;
      }

      this.form?.classList.toggle('product-form--text-field-incomplete', !valid);
      this.productForm?.classList.toggle('product-form--text-field-incomplete', !valid);

      if (!this.checkoutGate) {
        this.ensureCheckoutGate();
      }

      const paymentRoot = this.getPaymentButtonRoot();
      if (paymentRoot) {
        paymentRoot.hidden = !valid;
        paymentRoot.setAttribute('aria-hidden', valid ? 'false' : 'true');
      }

      if (this.checkoutGate) {
        this.checkoutGate.hidden = valid;
        this.checkoutGate.tabIndex = valid ? -1 : 0;
      }
    }

    blockIncompleteCheckout() {
      const result = this.validate();
      if (result.ok) {
        this.syncCheckoutGate();
        return;
      }

      this.showError(result.message);
      this.input?.focus();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    getValue() {
      return (this.input?.value || '').trim();
    }

    onInput() {
      if (this.onlyNumbers && this.input) {
        this.input.value = this.input.value.replace(/\D/g, '');
      }

      this.syncHiddenInput();
      this.clearError();
      this.updateMaxHint();
      this.syncCheckoutGate();
    }

    updateMaxHint() {
      if (!this.hintEl || !this.input) return;

      const length = this.input.value.length;
      const atLimit = length >= this.maxChars;

      this.hintEl.classList.toggle('hidden', !atLimit);
      if (atLimit) {
        this.hintEl.textContent = `Massimo ${this.maxChars} caratteri`;
      }
    }

    syncHiddenInput() {
      if (!this.hiddenInput) return;

      const value = this.getValue();
      if (!value) {
        // Campo vuoto: non mandare una property vuota in carrello.
        this.hiddenInput.value = '';
        this.hiddenInput.disabled = !this.required;
        this.syncSnapshotInput('');
        return;
      }

      this.hiddenInput.disabled = false;
      this.hiddenInput.value = value;
      this.syncSnapshotInput(value);
    }

    syncSnapshotInput(value) {
      if (!this.snapshotInput) return;

      const snapshot = value
        ? serializeMelideSnapshot([[this.propertyName, value]], {
            productTitle: this.productTitle,
            variantId: this.getVariantId(),
            variantTitle: this.getVariantTitle(),
          })
        : '';

      if (!snapshot) {
        this.snapshotInput.value = '';
        this.snapshotInput.disabled = true;
        this.syncAttributeInput('');
        return;
      }

      this.snapshotInput.disabled = false;
      this.snapshotInput.value = snapshot;
      this.syncAttributeInput(snapshot);
    }

    syncAttributeInput(snapshot) {
      if (!this.attributeInput) return;

      const variantId = this.getVariantId();
      if (!snapshot || !variantId) {
        this.attributeInput.value = '';
        this.attributeInput.disabled = true;
        return;
      }

      this.attributeInput.disabled = false;
      this.attributeInput.name = `attributes[${MELIDE_CART_ATTR_PREFIX}${variantId}]`;
      this.attributeInput.value = snapshot;
    }

    validate() {
      const value = this.getValue();

      // Facoltativo e vuoto → ok (niente property).
      if (!value) {
        if (!this.required) return { ok: true, value: '' };
        return { ok: false, message: 'Compila il campo obbligatorio.' };
      }

      if (value.length < this.minChars) {
        return {
          ok: false,
          message: `Inserisci almeno ${this.minChars} caratter${this.minChars === 1 ? 'e' : 'i'}.`,
        };
      }

      if (value.length > this.maxChars) {
        return { ok: false, message: `Massimo ${this.maxChars} caratteri.` };
      }

      if (this.onlyNumbers && !/^\d+$/.test(value)) {
        return { ok: false, message: 'Inserisci solo numeri.' };
      }

      return { ok: true, value };
    }

    showError(message) {
      if (!this.errorEl) return;

      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
      this.input?.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (!this.errorEl) return;

      this.errorEl.textContent = '';
      this.errorEl.classList.add('hidden');
      this.input?.removeAttribute('aria-invalid');
    }

    onFormSubmit(event) {
      const result = this.validate();

      if (!result.ok) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(result.message);
        this.input?.focus();
        this.syncCheckoutGate();
        return;
      }

      this.syncHiddenInput();
      this.syncCheckoutGate();
    }
  }

  customElements.define('product-text-field', ProductTextField);
})();
